import { useState, useCallback, useEffect } from 'react';
import {
	TrashIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/outline';
import { getProjectData } from '@appmaker/core/store/project';
import { SketchPicker } from 'react-color';
import Preview from './Preview'; // Will create this later
import { useCurrentProjectExtensionInstalls, useUpdateExtensionInstall } from '../../../hooks/useAppmakerGql';

const ColorPicker = ({ color, onChange, label }) => {
	const [showPicker, setShowPicker] = useState(false);

	const handleColorChange = (colorObj) => {
		console.log('Color changed:', colorObj);
		onChange(colorObj);
	};

	return (
		<div>
			<label className="block text-sm font-medium text-gray-700 mb-2">
				{label}
			</label>
			<div className="relative">
				<div className="flex flex-row items-center gap-2 border border-gray-300 rounded-md py-2 px-6">
					<div
						className="h-6 w-6 rounded-md border border-gray-300 cursor-pointer"
						style={{ backgroundColor: color }}
						onClick={() => setShowPicker(!showPicker)}
					/>
					<div className="flex flex-col">
						<span className="text-sm">{color}</span>
					</div>
				</div>
				{showPicker && (
					<div className="absolute z-10 mt-2">
						<div
							className="fixed inset-0"
							onClick={() => setShowPicker(false)}
						/>
						<SketchPicker 
							color={color} 
							onChange={handleColorChange}
							presetColors={[
								'#2e17a5', '#7568b6', '#f0eef8', '#2c0fbe', '#0a0a0a',
								'#8c7cde', '#42338b', '#f0eef8', '#2c2a38', '#ebeaf1',
								'#3f2e90', '#f4f3f8', '#5644b0', '#FFFFFF', '#000000'
							]}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

const WelcomeBanner = () => {
	const { projectData } = getProjectData();
	const { icon: projectIcon } = projectData || {}; // Added a fallback for projectData

	// Fetch API data
	const { data: extensionInstalls, isLoading, error } = useCurrentProjectExtensionInstalls({
		extension: {
			handle: {
				equals: 'plobal' 
			}
		}
	});

	// Update mutation
	const updateMutation = useUpdateExtensionInstall({
		onSuccess: (data) => {
			console.log('Welcome Banner updated successfully:', data);
		},
		onError: (error) => {
			console.error('Failed to update Welcome Banner:', error);
		}
	});

	// Extract welcome_banner data from API
	const apiData = extensionInstalls?.[0]?.settings?.welcome_banner || null;
	const extensionInstallId = extensionInstalls?.[0]?.id;

	const getInitialFormData = () => {
		if (apiData) {
			return {
				icon: apiData.app_icon?.url || projectIcon || '',
				step1: {
					title: apiData.onboarding_welcome?.title || 'Welcomes',
					description: apiData.onboarding_welcome?.description || 'Discover amazing products',
				},
				step2: {
					title: apiData.onboarding_discount_offer?.title || 'Get a sepecial discount',
					description: apiData.onboarding_discount_offer?.description || 'Use the coupons',
					couponCode: apiData.onboarding_discount_offer?.coupon_code || 'WELCOME20',
					couponPercentage: apiData.onboarding_discount_offer?.coupon_percentage || '20',
				},
				advancedSettings: {
					yesButton: {
						text: apiData.advanced_settings?.yes_button?.button_text || 'Yes',
						backgroundColor: apiData.advanced_settings?.yes_button?.background_color || '#8c7cde',
						textColor: apiData.advanced_settings?.yes_button?.text_color || '#42338b',
					},
					noButton: {
						text: apiData.advanced_settings?.no_button?.button_text || 'No',
						backgroundColor: apiData.advanced_settings?.no_button?.background_color || '#f0eef8',
						textColor: apiData.advanced_settings?.no_button?.text_color || '#2c2a38',
					},
					couponCodeButton: {
						text: apiData.advanced_settings?.coupon_code_button?.button_text || 'Apply Coupoun',
						backgroundColor: apiData.advanced_settings?.coupon_code_button?.background_color || '#ebeaf1',
						textColor: apiData.advanced_settings?.coupon_code_button?.text_color || '#3f2e90',
					},
					doneButton: {
						text: apiData.advanced_settings?.done_button?.button_text || 'Done',
						backgroundColor: apiData.advanced_settings?.done_button?.background_color || '#f4f3f8',
						textColor: apiData.advanced_settings?.done_button?.text_color || '#5644b0',
					},
				},
			};
		} else {
			// Fallback values when no API data
			return {
				icon: projectIcon || '',
				step1: {
					title: 'Welcomes',
					description: 'Discover amazing products',
				},
				step2: {
					title: 'Get a sepecial discount',
					description: 'Use the coupons',
					couponCode: 'WELCOME20',
					couponPercentage: '20',
				},
				advancedSettings: {
					yesButton: {
						text: 'Yes',
						backgroundColor: '#8c7cde',
						textColor: '#42338b',
					},
					noButton: {
						text: 'No', 
						backgroundColor: '#f0eef8',
						textColor: '#2c2a38',
					},
					couponCodeButton: {
						text: 'Apply Coupoun',
						backgroundColor: '#ebeaf1',
						textColor: '#3f2e90',
					},
					doneButton: {
						text: 'Done',
						backgroundColor: '#f4f3f8',
						textColor: '#5644b0',
					},
				},
			};
		}
	};

	const [formData, setFormData] = useState(getInitialFormData());
	const [isDragging, setIsDragging] = useState(false);
	const [currentPreviewStep, setCurrentPreviewStep] = useState(1);
	const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

	// Update form data when API data loads
	useEffect(() => {
		if (!isLoading && extensionInstalls) {
			setFormData(getInitialFormData());
		}
	}, [extensionInstalls, isLoading]);

	const handleChange = (e) => {
		const { name, value, files, type, checked } = e.target;
		console.log('Form field changed:', { name, value, type, checked });
		const nameParts = name.split('.');

		if (files && files[0]) {
			setFormData((prev) => ({
				...prev,
				icon: files[0],
			}));
		} else if (nameParts.length === 2) {
			const [section, field] = nameParts;
			setFormData((prev) => {
				const newData = {
					...prev,
					[section]: {
						...prev[section],
						[field]: type === 'checkbox' ? checked : value,
					},
				};
				console.log(`Updated ${section}.${field}:`, newData[section][field]);
				return newData;
			});
		} else if (nameParts.length === 3) {
			const [section, subSection, field] = nameParts;
			setFormData((prev) => {
				const newData = {
					...prev,
					[section]: {
						...prev[section],
						[subSection]: {
							...prev[section][subSection],
							[field]: type === 'checkbox' ? checked : value,
						},
					},
				};
				console.log(`Updated ${section}.${subSection}.${field}:`, newData[section][subSection][field]);
				return newData;
			});
		} else {
			setFormData((prev) => {
				const newData = {
					...prev,
					[name]: type === 'checkbox' ? checked : value,
				};
				console.log(`Updated ${name}:`, newData[name]);
				return newData;
			});
		}
	};

	const handleColorChange = (color, name) => {
		console.log('handleColorChange called:', { color, name });
		const nameParts = name.split('.');
		if (nameParts.length === 3) {
			const [section, subSection, field] = nameParts; // e.g., advancedSettings.yesButton.backgroundColor
			setFormData((prev) => {
				const newData = {
					...prev,
					[section]: {
						...prev[section],
						[subSection]: {
							...prev[section][subSection],
							[field]: color.hex,
						},
					},
				};
				console.log(`Updated ${name}:`, newData[section][subSection][field]);
				return newData;
			});
		} else {
			console.warn('Invalid color field name:', name);
		}
	};

	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback((e) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file && file.type.startsWith('image/')) {
			setFormData((prev) => ({
				...prev,
				icon: file,
			}));
		}
	}, []);

	const removeIcon = () => {
		setFormData((prev) => ({
			...prev,
			icon: '',
		}));
	};

	const getIconUrl = () => {
		if (formData.icon instanceof File) {
			return URL.createObjectURL(formData.icon);
		}
		return formData.icon;
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		console.log('Form submitted with data:', formData);
		
		// Transform form data back to API structure
		const apiFormattedData = {
			welcome_banner: {
				app_icon: {
					url: formData.icon,
					// If it's a new file upload, we'd handle this differently in real implementation
					id: apiData?.app_icon?.id || null,
					meta: apiData?.app_icon?.meta || null,
					title: apiData?.app_icon?.title || ""
				},
				onboarding_welcome: {
					title: formData.step1.title,
					description: formData.step1.description,
				},
				onboarding_discount_offer: {
					title: formData.step2.title,
					description: formData.step2.description,
					coupon_code: formData.step2.couponCode,
					coupon_percentage: formData.step2.couponPercentage,
				},
				advanced_settings: {
					yes_button: {
						button_text: formData.advancedSettings.yesButton.text,
						background_color: formData.advancedSettings.yesButton.backgroundColor,
						text_color: formData.advancedSettings.yesButton.textColor,
					},
					no_button: {
						button_text: formData.advancedSettings.noButton.text,
						background_color: formData.advancedSettings.noButton.backgroundColor,
						text_color: formData.advancedSettings.noButton.textColor,
					},
					coupon_code_button: {
						button_text: formData.advancedSettings.couponCodeButton.text,
						background_color: formData.advancedSettings.couponCodeButton.backgroundColor,
						text_color: formData.advancedSettings.couponCodeButton.textColor,
					},
					done_button: {
						button_text: formData.advancedSettings.doneButton.text,
						background_color: formData.advancedSettings.doneButton.backgroundColor,
						text_color: formData.advancedSettings.doneButton.textColor,
					},
				},
			}
		};

		console.log('API formatted data:', apiFormattedData);

		// Send mutation if we have an extension install ID
		if (extensionInstallId) {
			console.log('Updating with extension install ID:', extensionInstallId);
			// Merge with existing settings to preserve other data
			const currentSettings = extensionInstalls[0].settings || {};
			const updatedSettings = {
				...currentSettings,
				...apiFormattedData
			};
			
			console.log('Final settings to update:', updatedSettings);
		
			updateMutation.mutate({
				where: { id: extensionInstallId },
				data: { settings: updatedSettings }
			});
		} else {
			console.error('No extension install ID found. Cannot update settings.');
		}
	};

	// Show loading state
	if (isLoading) {
		return (
			<div className="p-6">
				<div className="mb-6">
					<div className="animate-pulse">
						<div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
						<div className="h-4 bg-gray-200 rounded w-1/2"></div>
					</div>
				</div>

				<div className="flex flex-col lg:flex-row gap-8">
					{/* Form Section Skeleton */}
					<div className="flex-1 space-y-6">
						{/* Icon Upload Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
							<div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
								<div className="flex justify-center">
									<div className="h-32 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
								</div>
							</div>
						</div>

						{/* Step 1 Skeleton */}
						<div className="border border-gray-200 rounded-md p-4">
							<div className="h-6 bg-gray-200 rounded w-16 mb-4"></div>
							<div className="space-y-4">
								<div>
									<div className="h-4 bg-gray-200 rounded w-12 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div>
									<div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
									<div className="h-20 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
						</div>

						{/* Step 2 Skeleton */}
						<div className="border border-gray-200 rounded-md p-4">
							<div className="h-6 bg-gray-200 rounded w-16 mb-4"></div>
							<div className="space-y-4">
								<div>
									<div className="h-4 bg-gray-200 rounded w-12 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div>
									<div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
									<div className="h-20 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div>
									<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div>
									<div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
						</div>

						{/* Advanced Settings Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-36 mb-2"></div>
							<div className="space-y-4">
								{/* Yes Button */}
								<div>
									<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
									<div className="flex gap-4">
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
											<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
									</div>
								</div>

								{/* No Button */}
								<div>
									<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
									<div className="flex gap-4">
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
											<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
									</div>
								</div>

								{/* Coupon Code Button */}
								<div>
									<div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
									<div className="flex gap-4">
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
											<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
									</div>
								</div>

								{/* Done Button */}
								<div>
									<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
									<div className="flex gap-4">
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
											<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="flex-1">
											<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
											<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Save Button Skeleton */}
						<div className="pt-4">
							<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
						</div>
					</div>

					{/* Preview Section Skeleton */}
					<div className="w-80">
						<div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
						<div className="bg-gray-100 rounded-lg p-4 h-96">
							<div className="space-y-4">
								<div className="flex items-center gap-3">
									<div className="h-12 w-12 bg-gray-200 rounded-lg animate-pulse"></div>
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
										<div className="h-3 bg-gray-200 rounded w-1/2"></div>
									</div>
								</div>
								<div className="space-y-2">
									<div className="h-8 bg-gray-200 rounded animate-pulse"></div>
									<div className="h-8 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className="mb-6 mt-6">
				<div className="bg-red-50 border border-red-200 rounded-md p-4">
					<h2 className="text-lg font-medium text-red-800">Error Loading Data</h2>
					<p className="text-red-600">{error.message}</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="mb-6 mt-6">
				<div>
					<h2 className="text-2xl font-bold text-gray-900">Welcome Banner</h2>
					<p className="mt-1 text-sm text-gray-500">
						Configure the welcome banner for your users.
					</p>
				</div>
			</div>

			<div className="flex flex-col lg:flex-row gap-8">
				{/* Form Section */}
				<div className="flex-1 p-0">
					<form
						className="space-y-6"
						onSubmit={handleSubmit}
						id="welcomeBannerForm"
					>
						{/* Icon Upload */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Upload Icon
							</label>
							<div
								className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg ${
									isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
								}`}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
							>
								<div className="space-y-1 text-center">
									{formData.icon ? (
										<div className="relative">
											<img
												src={getIconUrl()}
												alt="App Icon"
												className="mx-auto h-32 w-32 object-contain rounded-lg"
											/>
											<button
												type="button"
												onClick={removeIcon}
												className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none"
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										</div>
									) : (
										<>
											<svg
												className="mx-auto h-12 w-12 text-gray-400"
												stroke="currentColor"
												fill="none"
												viewBox="0 0 48 48"
												aria-hidden="true"
											>
												<path
													d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
													strokeWidth={2}
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
											<div className="flex text-sm text-gray-600">
												<label
													htmlFor="icon"
													className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
												>
													<span>Upload a file</span>
													<input
														id="icon"
														name="icon"
														type="file"
														className="sr-only"
														onChange={handleChange}
														accept="image/*"
													/>
												</label>
												<p className="pl-1">or drag and drop</p>
											</div>
											<p className="text-xs text-gray-500">
												PNG, JPG, GIF up to 10MB
											</p>
										</>
									)}
								</div>
							</div>
						</div>

						{/* Step 1 */}
						<div className="space-y-4 p-4 border border-gray-200 rounded-md">
							<h3 className="text-lg font-medium leading-6 text-gray-900">
								Step 1
							</h3>
							<div>
								<label
									htmlFor="step1.title"
									className="block text-sm font-medium text-gray-700"
								>
									Title
								</label>
								<input
									type="text"
									name="step1.title"
									id="step1.title"
									value={formData.step1.title}
									onChange={handleChange}
									className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
								/>
							</div>
							<div>
								<label
									htmlFor="step1.description"
									className="block text-sm font-medium text-gray-700"
								>
									Description
								</label>
								<textarea
									name="step1.description"
									id="step1.description"
									rows={3}
									value={formData.step1.description}
									onChange={handleChange}
									className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
								/>
							</div>
						</div>

						{/* Step 2 */}
						<div className="space-y-4 p-4 border border-gray-200 rounded-md">
							<h3 className="text-lg font-medium leading-6 text-gray-900">
								Step 2
							</h3>
							<div>
								<label
									htmlFor="step2.title"
									className="block text-sm font-medium text-gray-700"
								>
									Title
								</label>
								<input
									type="text"
									name="step2.title"
									id="step2.title"
									value={formData.step2.title}
									onChange={handleChange}
									className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
								/>
							</div>
							<div>
								<label
									htmlFor="step2.description"
									className="block text-sm font-medium text-gray-700"
								>
									Description
								</label>
								<textarea
									name="step2.description"
									id="step2.description"
									rows={3}
									value={formData.step2.description}
									onChange={handleChange}
									className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
								/>
							</div>
							<div>
								<label
									htmlFor="step2.couponCode"
									className="block text-sm font-medium text-gray-700"
								>
									Coupon Code
								</label>
								<input
									type="text"
									name="step2.couponCode"
									id="step2.couponCode"
									value={formData.step2.couponCode}
									onChange={handleChange}
									className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
								/>
							</div>
							<div>
								<label
									htmlFor="step2.couponPercentage"
									className="block text-sm font-medium text-gray-700"
								>
									Coupon Percentage
								</label>
								<select
									name="step2.couponPercentage"
									id="step2.couponPercentage"
									value={formData.step2.couponPercentage}
									onChange={handleChange}
									className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
								>
									{[5, 10, 15, 20, 25].map((perc) => (
										<option key={perc} value={perc}>
											{perc}%
										</option>
									))}
								</select>
							</div>
						</div>

						{/* Advanced Settings */}
						<div className="border-t border-gray-200 pt-4">
							<button
								type="button"
								className="w-full flex items-center justify-between"
								onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
							>
								<h3 className="text-lg font-medium leading-6 text-gray-900">
									Advanced Settings
								</h3>
								{showAdvancedSettings ? (
									<ChevronUpIcon className="h-6 w-6 text-gray-600" />
								) : (
									<ChevronDownIcon className="h-6 w-6 text-gray-600" />
								)}
							</button>
							{showAdvancedSettings && (
								<div className="space-y-4 py-4">
									{/* Yes Button */}
									<div className="space-y-2 p-3 border border-gray-100 rounded">
										<h4 className="text-md font-medium text-gray-800">
											"Yes" Button
										</h4>
										<div>
											<label
												htmlFor="advancedSettings.yesButton.text"
												className="block text-sm font-medium text-gray-700"
											>
												Button Text
											</label>
											<input
												type="text"
												name="advancedSettings.yesButton.text"
												id="advancedSettings.yesButton.text"
												value={formData.advancedSettings.yesButton.text}
												onChange={handleChange}
												className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
											/>
										</div>
										<div className="flex flex-row items-center justify-between gap-2">
											<ColorPicker
												label="Background Color"
												color={
													formData.advancedSettings.yesButton.backgroundColor
												}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.yesButton.backgroundColor'
													)
												}
											/>
											<ColorPicker
												label="Text Color"
												color={formData.advancedSettings.yesButton.textColor}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.yesButton.textColor'
													)
												}
											/>
										</div>
									</div>

									{/* No Button */}
									<div className="space-y-2 p-3 border border-gray-100 rounded">
										<h4 className="text-md font-medium text-gray-800">
											"No" Button
										</h4>
										<div>
											<label
												htmlFor="advancedSettings.noButton.text"
												className="block text-sm font-medium text-gray-700"
											>
												Button Text
											</label>
											<input
												type="text"
												name="advancedSettings.noButton.text"
												id="advancedSettings.noButton.text"
												value={formData.advancedSettings.noButton.text}
												onChange={handleChange}
												className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
											/>
										</div>
										<div className="flex flex-row items-center justify-between gap-2">
											<ColorPicker
												label="Background Color"
												color={
													formData.advancedSettings.noButton.backgroundColor
												}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.noButton.backgroundColor'
													)
												}
											/>
											<ColorPicker
												label="Text Color"
												color={formData.advancedSettings.noButton.textColor}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.noButton.textColor'
													)
												}
											/>
										</div>
									</div>

									{/* Coupon Code Button */}
									<div className="space-y-2 p-3 border border-gray-100 rounded">
										<h4 className="text-md font-medium text-gray-800">
											"Coupon Code" Button
										</h4>
										<div>
											<label
												htmlFor="advancedSettings.couponCodeButton.text"
												className="block text-sm font-medium text-gray-700"
											>
												Button Text
											</label>
											<input
												type="text"
												name="advancedSettings.couponCodeButton.text"
												id="advancedSettings.couponCodeButton.text"
												value={formData.advancedSettings.couponCodeButton.text}
												onChange={handleChange}
												className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
											/>
										</div>
										<div className="flex flex-row items-center justify-between gap-2">
											<ColorPicker
												label="Background Color"
												color={
													formData.advancedSettings.couponCodeButton
														.backgroundColor
												}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.couponCodeButton.backgroundColor'
													)
												}
											/>
											<ColorPicker
												label="Text Color"
												color={
													formData.advancedSettings.couponCodeButton.textColor
												}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.couponCodeButton.textColor'
													)
												}
											/>
										</div>
									</div>

									{/* Done Button */}
									<div className="space-y-2 p-3 border border-gray-100 rounded">
										<h4 className="text-md font-medium text-gray-800">
											"Done" Button
										</h4>
										<div>
											<label
												htmlFor="advancedSettings.doneButton.text"
												className="block text-sm font-medium text-gray-700"
											>
												Button Text
											</label>
											<input
												type="text"
												name="advancedSettings.doneButton.text"
												id="advancedSettings.doneButton.text"
												value={formData.advancedSettings.doneButton.text}
												onChange={handleChange}
												className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
											/>
										</div>
										<div className="flex flex-row items-center justify-between gap-2">
											<ColorPicker
												label="Background Color"
												color={
													formData.advancedSettings.doneButton.backgroundColor
												}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.doneButton.backgroundColor'
													)
												}
											/>
											<ColorPicker
												label="Text Color"
												color={formData.advancedSettings.doneButton.textColor}
												onChange={(color) =>
													handleColorChange(
														color,
														'advancedSettings.doneButton.textColor'
													)
												}
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					</form>
				</div>

				{/* Preview Section Placeholder - will be implemented next */}
				<div className="w-full lg:w-96 flex flex-col">
					<Preview
						formData={formData}
						getIconUrl={getIconUrl}
						currentStep={currentPreviewStep}
					/>
					{/* Carousel controls for preview steps */}
					<div className="mt-6">
						{/* Visual indicator for current step (optional, can be enhanced) */}
						<div className="flex space-x-2 justify-center mt-2">
							<button
								type="button"
								onClick={() => setCurrentPreviewStep(1)}
								className={`block w-2 h-2 rounded-full ${
									currentPreviewStep === 1 ? 'bg-gray-700' : 'bg-gray-300'
								}`}
							/>
							<button
								type="button"
								onClick={() => setCurrentPreviewStep(2)}
								className={`block w-2 h-2 rounded-full ${
									currentPreviewStep === 2 ? 'bg-gray-700' : 'bg-gray-300'
								}`}
							/>
						</div>
					</div>
					{/* MOVED BUTTONS */}
					<div className="mt-6">
						<div className="flex justify-center">
							<button
								type="submit"
								form="welcomeBannerForm"
								disabled={updateMutation.isLoading}
								className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{updateMutation.isLoading ? (
									<>
										<svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										Saving...
									</>
								) : (
									'Save & Preview'
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default WelcomeBanner;
